import { z } from 'zod'

import {
  FORCE_CURRENT_USER,
  getEntityRegistry,
  getForceAtCreate,
  isCreatable,
} from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import {
  checkRateLimit,
  recordCall,
} from '@/Atomic/Widgets/AiChatBot/core/engine/pipeline/write/rate-limiter'
import { clearToolCache } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-cache'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type {
  SessionContext,
  ToolResultContent,
  WriteNeedsInput,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import { mapFieldsFromIntent } from '@/Atomic/Widgets/AiChatBot/core/tools/schema/field-mapper'
import {
  requireDataClassName,
  resolveDataClass,
  type ScrivitoAccessor,
} from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'

type CreateEntityArgs = {
  type: string
  /** Partial field values collected so far (field name → string value). */
  fields: Record<string, string>
}

/**
 * Create a new CRM entity record.
 *
 * Conversational flow:
 * 1. If required fields are missing → return needs_input so the model asks in chat.
 * 2. All required fields present → pause the pipeline and show a ConfirmationCard.
 *    Execution resumes when the user clicks Confirm (resolved: true) or Cancel (resolved: false).
 * 3. On confirm → write to PISA via Scrivito DataClass.create()
 *
 * concurrencySafe: false — creates must never run concurrently.
 */
export const createEntityTool = defineTool({
  name: 'create_entity',
  description:
    'Create a new CRM record. The type must be a canonical entity name: Company, Person, Lead, Ticket, Activity, Opportunity, Quote, Order, or List (pass type "List" to create a segment — see the segment rules). ' +
    'If required fields are missing the tool returns needs_input listing each missing field with its label, type, and allowed options — ask the user for all missing fields in ONE message, then call this tool again immediately. ' +
    'For relation fields (customer, responsibleAgent, etc.) always pass the record _id from confirmed_entities or a prior tool result — NEVER pass a display name or label. ' +
    'To set a person/owner reference field to the acting user ("responsible is me", "assign it to me"), pass the literal "@currentUser" as that field\'s value — do NOT ask the user for their name and do NOT guess or copy an id. ' +
    'Do NOT pass a confirmed parameter — confirmation is handled by the UI after all required fields are present. ' +
    'To create AND immediately LINK the new record (e.g. "create an activity for this person"): call this tool FIRST and wait for it to return the new id. ' +
    'Only AFTER it has returned may you call relate_entity with that id — NEVER in the same turn as this call, and NEVER with a placeholder or guessed id.',
  inputSchema: () =>
    z.object({
      type: z
        .string()
        .describe('Entity type to create, e.g. Ticket, Activity, Opportunity'),
      fields: z
        .record(z.string(), z.string())
        .describe('Field values to set on the new record (field name → value)'),
    }),
  concurrencySafe: false,
  cacheable: false,

  async run(
    rawArgs: unknown,
    ctx: SessionContext,
    toolCallId?: string,
  ): Promise<ToolResultContent | WriteNeedsInput | { error: string }> {
    // FunctionDefinition<unknown> contravariance — cast args inside run(), per project pattern
    const args = rawArgs as CreateEntityArgs
    const { type: rawType, fields } = args

    const type = requireDataClassName(rawType)
    if (!type) {
      return { error: `Unknown entity type: ${rawType}` }
    }

    if (!isCreatable(type)) {
      return { error: `Creating ${type} records is not supported.` }
    }

    const rateCheck = checkRateLimit('creates')
    if (!rateCheck.allowed) {
      return { kind: 'text', content: rateCheck.message ?? '' }
    }

    // Force-set registry-defined fields before mapping. These override any value the
    // model supplied (e.g. List `type` is fixed to `dynamic` so `static` can't be
    // produced); FORCE_CURRENT_USER resolves to the acting user for owner fields. If
    // that sentinel can't resolve, abort with an explicit error rather than silently
    // dropping a forced owner field (mirrors relate_entity). The field is required by
    // the registry, so proceeding without it would surface as a confusing PISA error.
    const forced = getForceAtCreate(type)
    const intentParams: Record<string, string> = { ...fields }
    if (forced) {
      for (const [key, forcedValue] of Object.entries(forced)) {
        if (forcedValue === FORCE_CURRENT_USER) {
          if (!ctx.currentUserId) {
            return {
              error: `Could not determine the current user to set the required "${key}" field. Ask the user to retry once signed in — do not set it manually.`,
            }
          }
          intentParams[key] = ctx.currentUserId
        } else {
          intentParams[key] = forcedValue
        }
      }
    }

    const mapping = await mapFieldsFromIntent({
      entityType: type,
      intentParams,
      operation: 'create',
      fieldGroups: getEntityRegistry()[type]?.fieldGroups,
      currentUserId: ctx.currentUserId,
    })
    if (!mapping.ok) {
      return { error: mapping.message }
    }

    // A reference id that resolves to no live record (a stale or guessed id) must
    // never be written. Reject so the model re-resolves the record via search_entities.
    if (mapping.unresolvedReferences.length > 0) {
      const labels = mapping.unresolvedReferences.map((r) => r.label).join(', ')
      return {
        error: `Could not find the record referenced by ${labels}. Look it up with search_entities and pass the matching record's _id — do not guess an id.`,
      }
    }

    // Missing required fields, unrecognised fields, or enum values that matched no
    // option → model asks in chat. invalidValues must block the write even when
    // nothing is missing: a guessed PSA_* code is never written or shown.
    if (
      mapping.missing.length > 0 ||
      mapping.invalidValues.length > 0 ||
      mapping.invalid.length > 0
    ) {
      return {
        needs_input: {
          missing: mapping.missing,
          ...(mapping.invalid.length > 0
            ? {
                unrecognized: mapping.invalid.map((key) => ({
                  suppliedKey: key,
                  suppliedValue: String(fields[key] ?? ''),
                })),
              }
            : {}),
          ...(mapping.invalidValues.length > 0
            ? { invalidValues: mapping.invalidValues }
            : {}),
        },
      }
    }

    // All fields resolved → pause pipeline, show ConfirmationCard
    const id = toolCallId ?? '_unknown'
    const confirmed = await new Promise<boolean>((resolve, reject) => {
      ctx.registerToolCallResolver?.(
        id,
        {
          type: 'confirm',
          toolCallId: id,
          spec: {
            title: `Create ${type}`,
            rows: Object.entries(mapping.displayMapped).map(
              ([key, display]) => ({
                label: mapping.labelMap[key] ?? key,
                value: display,
                source: 'resolved' as const,
              }),
            ),
          },
        },
        (result: unknown) => resolve(result === true),
        reject,
      )
    })

    if (!confirmed) {
      return { kind: 'text', content: `Cancelled creating ${type}.` }
    }

    const dataClass = resolveDataClass(type)
    if (!dataClass) {
      return { error: `DataClass not found for ${type}` }
    }

    try {
      const created = await getHostRuntime().data.create(type, mapping.mapped)
      clearToolCache()
      // Refresh the per-DataClass query caches the app reads — otherwise
      // detail-page tabs (e.g. a company's Tickets tab, backed by the junction
      // ContactProjectRelation) show stale data until a manual page reload.
      getHostRuntime().data.invalidate(type)
      recordCall('creates')
      // warning: adapter.create returns the new record opaquely (unknown); narrow to
      // the shared accessor to read its id. urlFor accepts the opaque item directly.
      const createdId = (created as ScrivitoAccessor).id()
      const url = await getHostRuntime().navigation.urlFor(created)
      return {
        kind: 'text',
        content: url
          ? `Successfully created ${type} (id: ${createdId}). [Open ${type}](${url})`
          : `Successfully created ${type} (id: ${createdId}). The record is now available in the CRM.`,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      return {
        kind: 'text',
        content: `Failed to create ${type}: ${message}`,
      }
    }
  },
})
